import { useCallback, useEffect, useMemo, useState } from 'react';
import { Loader2, RefreshCcw, Search, ServerCrash, Square } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useAppSelector } from '@/app/hooks';
import { RootState } from '@/app/store';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select';
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table';
import {
  fetchAdminNovapostCachedDivisions,
  fetchAdminNovapostCacheStatuses,
  refreshAdminNovapostCountryCache,
  refreshAllAdminNovapostCaches,
  stopAdminNovapostCacheRefresh,
  type AdminNovapostCachedDivision,
  type AdminNovapostCacheState,
  type AdminNovapostCacheStatus,
} from '@/services/adminService';
import { analytics } from '@/services/analytics';
import { logging } from '@/services/logging';
import { useToast } from '@/hooks/use-toast';

const COUNTRY_OPTIONS = ['UA', 'PL', 'DE', 'LT', 'LV', 'HU', 'MD', 'RO', 'SK', 'CZ', 'EE'] as const;

const formatDateTime = (value: string | null) => {
  if (!value) {
    return 'Never';
  }
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) {
    return value;
  }
  return new Intl.DateTimeFormat(undefined, {
    year: 'numeric',
    month: 'short',
    day: '2-digit',
    hour: '2-digit',
    minute: '2-digit',
  }).format(date);
};

const formatDuration = (durationMs: number | null) => {
  if (!durationMs || durationMs <= 0) {
    return 'n/a';
  }
  if (durationMs < 1000) {
    return `${durationMs} ms`;
  }
  return `${(durationMs / 1000).toFixed(durationMs >= 10000 ? 0 : 1)} s`;
};

const badgeVariantByState: Record<AdminNovapostCacheState, 'default' | 'secondary' | 'destructive' | 'outline'> = {
  READY: 'default',
  WARMING: 'secondary',
  STALE: 'outline',
  FAILED: 'destructive',
  MISSING: 'outline',
};

const AdminNovapostCachePage = () => {
  const navigate = useNavigate();
  const user = useAppSelector((state: RootState) => state.auth.user);
  const isAdmin = user?.role === 'ADMIN';
  const { toast } = useToast();

  const [activeTab, setActiveTab] = useState<'status' | 'divisions'>('status');

  const [statuses, setStatuses] = useState<ReadonlyArray<AdminNovapostCacheStatus>>([]);
  const [statusesState, setStatusesState] = useState<'idle' | 'loading' | 'error'>('idle');
  const [refreshAllPending, setRefreshAllPending] = useState(false);
  const [stopPending, setStopPending] = useState(false);
  const [countryPending, setCountryPending] = useState<string | null>(null);

  const [divisions, setDivisions] = useState<ReadonlyArray<AdminNovapostCachedDivision>>([]);
  const [divisionsState, setDivisionsState] = useState<'idle' | 'loading' | 'error'>('idle');
  const [selectedCountry, setSelectedCountry] = useState<string>('ALL');
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState('');
  const [divisionStatus, setDivisionStatus] = useState('');

  const loadStatuses = useCallback(async () => {
    try {
      setStatusesState('loading');
      const result = await fetchAdminNovapostCacheStatuses();
      setStatuses(result);
      setStatusesState('idle');
    } catch (error) {
      logging.error('Failed to load Nova Post cache statuses', { error });
      setStatusesState('error');
      toast({
        title: 'Failed to load Nova Post cache statuses',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  }, [toast]);

  const loadDivisions = useCallback(async () => {
    try {
      setDivisionsState('loading');
      const result = await fetchAdminNovapostCachedDivisions({
        countryCode: selectedCountry === 'ALL' ? undefined : selectedCountry,
        query: query || undefined,
        category: category || undefined,
        status: divisionStatus || undefined,
        limit: 200,
      });
      setDivisions(result);
      setDivisionsState('idle');
    } catch (error) {
      logging.error('Failed to load cached Nova Post divisions', { error });
      setDivisionsState('error');
      toast({
        title: 'Failed to load cached divisions',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    }
  }, [category, divisionStatus, query, selectedCountry, toast]);

  useEffect(() => {
    if (!isAdmin) {
      navigate('/', { replace: true });
      return;
    }
    void loadStatuses();
    void loadDivisions();
  }, [isAdmin, loadDivisions, loadStatuses, navigate]);

  const readyCount = useMemo(
    () => statuses.filter((entry) => entry.state === 'READY').length,
    [statuses],
  );

  const staleOrMissingCount = useMemo(
    () => statuses.filter((entry) => ['STALE', 'FAILED', 'MISSING'].includes(entry.state)).length,
    [statuses],
  );

  const categoryOptions = useMemo(() => {
    const discovered = new Set<string>();
    for (const division of divisions) {
      if (division.divisionCategory) {
        discovered.add(division.divisionCategory);
      }
    }
    if (category) {
      discovered.add(category);
    }
    return Array.from(discovered).sort((left, right) => left.localeCompare(right));
  }, [category, divisions]);

  const queuedCount = useMemo(
    () => statuses.filter((entry) => entry.isQueued).length,
    [statuses],
  );

  const hasActiveQueueWork = useMemo(
    () => statuses.some((entry) => entry.isRunning || entry.isQueued),
    [statuses],
  );

  const handleRefreshAll = async () => {
    analytics.track('button: ui.admin_novapost_cache_refresh_all');
    try {
      setRefreshAllPending(true);
      const result = await refreshAllAdminNovapostCaches();
      if (result.queuedCount === 0 && result.alreadyRunningCount === 0) {
        toast({
          title: 'Refresh skipped',
          description: hasActiveQueueWork
            ? 'Another Nova Post refresh is already running or waiting.'
            : 'No countries need refresh right now.',
        });
      } else {
        toast({
          title: 'Nova Post refresh queued',
          description: `Queued ${result.queuedCount} countr${result.queuedCount === 1 ? 'y' : 'ies'}; already running ${result.alreadyRunningCount}.`,
        });
      }
      await loadStatuses();
    } catch (error) {
      logging.error('Failed to refresh all Nova Post caches', { error });
      toast({
        title: 'Refresh all failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setRefreshAllPending(false);
    }
  };

  const handleRefreshCountry = async (countryCode: string) => {
    analytics.track('button: ui.admin_novapost_cache_refresh_country', { countryCode });
    try {
      setCountryPending(countryCode);
      const result = await refreshAdminNovapostCountryCache(countryCode);
      toast({
        title: result.blockedByActiveQueue
          ? 'Refresh blocked'
          : result.alreadyRunning
          ? 'Country refresh already running'
          : 'Country refresh queued',
        description: result.blockedByActiveQueue
          ? 'Another Nova Post refresh is already running or waiting.'
          : countryCode,
      });
      await loadStatuses();
    } catch (error) {
      logging.error('Failed to refresh Nova Post country cache', { error, countryCode });
      toast({
        title: `Refresh failed for ${countryCode}`,
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setCountryPending(null);
    }
  };

  const handleStopRefresh = async () => {
    analytics.track('button: ui.admin_novapost_cache_stop');
    try {
      setStopPending(true);
      const result = await stopAdminNovapostCacheRefresh();
      toast({
        title: 'Stop requested',
        description: `Cleared ${result.queuedCleared} waiting countr${result.queuedCleared === 1 ? 'y' : 'ies'} and signalled ${result.runningSignalled} running countr${result.runningSignalled === 1 ? 'y' : 'ies'}.`,
      });
      await loadStatuses();
    } catch (error) {
      logging.error('Failed to stop Nova Post refresh queue', { error });
      toast({
        title: 'Stop failed',
        description: error instanceof Error ? error.message : 'Unknown error',
        variant: 'destructive',
      });
    } finally {
      setStopPending(false);
    }
  };

  if (!isAdmin) {
    return null;
  }

  return (
    <div className="mx-auto w-full max-w-7xl px-4 pb-12 pt-8">
      <div className="grid gap-6">
        <Card>
          <CardHeader className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div>
              <CardTitle className="text-2xl">Nova Post master data</CardTitle>
              <CardDescription>
                DB-backed cache status, background refresh control, and searchable stored divisions.
              </CardDescription>
            </div>
            <div className="flex flex-wrap gap-2">
              <Button
                variant={activeTab === 'status' ? 'default' : 'outline'}
                onClick={() => setActiveTab('status')}
              >
                Status
              </Button>
              <Button
                variant={activeTab === 'divisions' ? 'default' : 'outline'}
                onClick={() => setActiveTab('divisions')}
              >
                Cached divisions
              </Button>
            </div>
          </CardHeader>
        </Card>

        {activeTab === 'status' ? (
          <>
            <Card>
              <CardContent className="grid gap-4 pt-6 md:grid-cols-3">
                <div className="rounded-lg border p-4">
                  <div className="text-sm text-muted-foreground">Ready countries</div>
                  <div className="mt-2 text-3xl font-semibold">{readyCount}</div>
                </div>
                <div className="rounded-lg border p-4">
                  <div className="text-sm text-muted-foreground">Need refresh</div>
                  <div className="mt-2 text-3xl font-semibold">{staleOrMissingCount}</div>
                </div>
                <div className="rounded-lg border p-4">
                  <div className="text-sm text-muted-foreground">Running / waiting</div>
                  <div className="mt-2 text-3xl font-semibold">
                    {statuses.filter((entry) => entry.isRunning).length} / {queuedCount}
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
                <div>
                  <CardTitle className="text-xl">Country cache status</CardTitle>
                  <CardDescription>
                    Startup and daily scheduler enqueue missing or stale countries at 3:00 AM Kyiv.
                  </CardDescription>
                </div>
                <div className="flex flex-wrap gap-2">
                  <Button variant="outline" onClick={() => void loadStatuses()} disabled={statusesState === 'loading'}>
                    {statusesState === 'loading' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <RefreshCcw className="mr-2 h-4 w-4" />}
                    Reload status
                  </Button>
                  <Button onClick={() => void handleRefreshAll()} disabled={refreshAllPending || hasActiveQueueWork}>
                    {refreshAllPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <ServerCrash className="mr-2 h-4 w-4" />}
                    Refresh all countries
                  </Button>
                  <Button variant="destructive" onClick={() => void handleStopRefresh()} disabled={stopPending || !hasActiveQueueWork}>
                    {stopPending ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Square className="mr-2 h-4 w-4" />}
                    Stop process
                  </Button>
                </div>
              </CardHeader>
              <CardContent>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>Country</TableHead>
                      <TableHead>State</TableHead>
                      <TableHead>Last success</TableHead>
                      <TableHead>Divisions</TableHead>
                      <TableHead>Settlements</TableHead>
                      <TableHead>Last duration</TableHead>
                      <TableHead>Latest run</TableHead>
                      <TableHead>Error</TableHead>
                      <TableHead className="text-right">Action</TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {statuses.map((entry) => (
                      <TableRow key={entry.countryCode}>
                        <TableCell className="font-medium">{entry.countryCode}</TableCell>
                        <TableCell>
                          <Badge variant={badgeVariantByState[entry.state]}>{entry.state}</Badge>
                        </TableCell>
                        <TableCell>{formatDateTime(entry.lastSuccessfulSyncAt)}</TableCell>
                        <TableCell>{entry.activeSnapshotDivisionCount}</TableCell>
                        <TableCell>{entry.activeSnapshotSettlementCount}</TableCell>
                        <TableCell>{formatDuration(entry.lastDurationMs)}</TableCell>
                        <TableCell>
                          {entry.isQueued && !entry.isRunning ? 'WAITING' : entry.latestRunStatus ?? 'Never'}
                        </TableCell>
                        <TableCell className="max-w-xs whitespace-normal text-xs text-muted-foreground">
                          {entry.latestRunErrorMessage ?? 'None'}
                        </TableCell>
                        <TableCell className="text-right">
                          <Button
                            variant="outline"
                            size="sm"
                            onClick={() => void handleRefreshCountry(entry.countryCode)}
                            disabled={countryPending === entry.countryCode || hasActiveQueueWork}
                          >
                            {countryPending === entry.countryCode ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : null}
                            Refresh
                          </Button>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </CardContent>
            </Card>
          </>
        ) : (
          <Card>
            <CardHeader>
              <CardTitle className="text-xl">Cached divisions search</CardTitle>
              <CardDescription>
                Search active country snapshots by country, text, category, and division status.
              </CardDescription>
            </CardHeader>
            <CardContent className="space-y-6">
              <div className="grid gap-4 md:grid-cols-4">
                <div className="space-y-2">
                  <Label htmlFor="country">Country</Label>
                  <Select value={selectedCountry} onValueChange={setSelectedCountry}>
                    <SelectTrigger id="country">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">All active snapshots</SelectItem>
                      {COUNTRY_OPTIONS.map((country) => (
                        <SelectItem key={country} value={country}>
                          {country}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="query">Search text</Label>
                  <Input
                    id="query"
                    value={query}
                    onChange={(event) => setQuery(event.target.value)}
                    placeholder="Settlement, office, address"
                  />
                </div>
                <div className="space-y-2">
                  <Label htmlFor="category">Category</Label>
                  <Select value={category || 'ALL'} onValueChange={(value) => setCategory(value === 'ALL' ? '' : value)}>
                    <SelectTrigger id="category">
                      <SelectValue placeholder="All categories" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="ALL">All categories</SelectItem>
                      {categoryOptions.map((option) => (
                        <SelectItem key={option} value={option}>
                          {option}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
                <div className="space-y-2">
                  <Label htmlFor="status">Division status</Label>
                  <Input
                    id="status"
                    value={divisionStatus}
                    onChange={(event) => setDivisionStatus(event.target.value)}
                    placeholder="e.g. Working"
                  />
                </div>
              </div>

              <div className="flex flex-wrap gap-2">
                <Button onClick={() => void loadDivisions()} disabled={divisionsState === 'loading'}>
                  {divisionsState === 'loading' ? <Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Search className="mr-2 h-4 w-4" />}
                  Search cached divisions
                </Button>
                <Button
                  variant="outline"
                  onClick={() => {
                    setSelectedCountry('ALL');
                    setQuery('');
                    setCategory('');
                    setDivisionStatus('');
                  }}
                >
                  Reset filters
                </Button>
              </div>

              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Country</TableHead>
                    <TableHead>Settlement</TableHead>
                    <TableHead>Division</TableHead>
                    <TableHead>Address</TableHead>
                    <TableHead>Category</TableHead>
                    <TableHead>Status</TableHead>
                    <TableHead>Flags</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {divisions.map((division) => (
                    <TableRow key={division.id}>
                      <TableCell className="font-medium">{division.countryCode}</TableCell>
                      <TableCell>
                        {division.settlementName ?? 'n/a'}
                        {division.settlementRegionName ? `, ${division.settlementRegionName}` : ''}
                      </TableCell>
                      <TableCell>
                        {division.divisionName ?? division.shortName ?? `#${division.sourceDivisionId}`}
                        {division.number ? ` (${division.number})` : ''}
                      </TableCell>
                      <TableCell className="max-w-sm whitespace-normal text-xs text-muted-foreground">
                        {division.address ?? 'n/a'}
                      </TableCell>
                      <TableCell>{division.divisionCategory ?? 'n/a'}</TableCell>
                      <TableCell>{division.divisionStatus ?? 'n/a'}</TableCell>
                      <TableCell className="text-xs text-muted-foreground">
                        {[
                          division.customerServiceAvailable ? 'service' : null,
                          division.prohibitedSending ? 'send blocked' : null,
                          division.prohibitedIssuance ? 'issue blocked' : null,
                        ].filter(Boolean).join(', ') || 'none'}
                      </TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

export default AdminNovapostCachePage;
